import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { isInt32, KeyValuePairStringIntFieldValue } from '..';
import {
  FieldWithKeyValuePairStringIntResponse,
  ValidationRuleResponse,
  ValidationTypeEnumResponse,
} from '../../../.generated/admin-api';
import { InputValidationResult } from '../..';
import { buildNumberTextFieldInputProps } from '.';
import { apiTranslate } from '../../../translations/translations';
import { OptionResponse } from '../../../.generated/admin-api/models/OptionResponse';

type KeyValuePairStringIntFieldProps = {
  field: FieldWithKeyValuePairStringIntResponse;
  defaultValue?: KeyValuePairStringIntFieldValue;
  disabled?: boolean;
  onChangeValidator: (
    value: KeyValuePairStringIntFieldValue,
  ) => InputValidationResult;
};

const useStyles = makeStyles((theme) => ({
  button: {},
  error: {
    color: theme.palette.error.main,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(2),
  },
  row: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  number: {
    paddingLeft: theme.spacing(2),
  },
}));

interface KeyValueItem {
  key: string;
  value: number | undefined;
}

interface Option {
  key: string;
  value: string;
}

const getAvailableOptions = (
  options: Array<OptionResponse> | undefined,
  selectedKeys: string[],
  currentKey: string,
): Option[] => {
  if (!options) {
    return [];
  }

  return options
    .filter(
      (option) =>
        !selectedKeys.includes(option.key) || currentKey === option.key,
    )
    .map((option) => {
      return { key: option.key, value: apiTranslate(option.text) };
    });
};

// TODO: DHM: Field does not support autofill, because it is managed. (Task 2219)
// It should be changed to update when props.value changes.
// See branch feature/mobile-string-int-autofill or commit 7a9b78c61e35ab56a4c1f127c8f57ca48cac1e8b with an not working solution (has since been reverted)
const KeyValuePairStringIntField: React.FC<KeyValuePairStringIntFieldProps> = (
  props,
) => {
  const { field, defaultValue, disabled, onChangeValidator } = props;

  const { t } = useTranslation(['translations']);

  const [keyValueItems, setKeyValueItems] = useState<KeyValueItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [
    maxAllowedOptionsValidation,
    setMaxAllowedOptionsValidation,
  ] = useState<ValidationRuleResponse>();
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState<boolean>(
    false,
  );

  const classes = useStyles();

  const options = field.options;

  const title = apiTranslate(field.title);

  const addEmptyKeyValueItem = () => {
    setKeyValueItems((prevKeyValueItems) => {
      let initialValue = 0;

      if (prevKeyValueItems.length === 0) {
        // If this is the first item and the field has a max sum,
        // we use that value as the default value
        const maxSumValidationRule = field.validationRules?.find(
          (validationRule) =>
            validationRule.type === ValidationTypeEnumResponse.MAX_SUM,
        );

        if (
          maxSumValidationRule &&
          maxSumValidationRule.validationValue !== undefined
        ) {
          initialValue = maxSumValidationRule.validationValue;
        }
      }

      return [...prevKeyValueItems, { key: '', value: initialValue }];
    });
  };

  const handleKeyChanged = (index: number, key: string) => {
    setKeyValueItems((prevKeyValueItems) => {
      const items = [...prevKeyValueItems];

      const currentItem = items[index];

      items[index] = {
        key,
        value: currentItem.value,
      };

      return items;
    });
  };

  const handleValueChange = (index: number, value: string) => {
    let numberValue: number | undefined = parseInt(value);
    if (isNaN(numberValue) || !isInt32(numberValue)) {
      numberValue = undefined;
    }

    setKeyValueItems((prevKeyValueItems) => {
      const items = [...prevKeyValueItems];

      const currentItem = items[index];

      items[index] = {
        key: currentItem.key,
        value: numberValue,
      };

      return items;
    });
  };

  useEffect(() => {
    if (defaultValue && Object.keys(defaultValue).length > 0) {
      const items: KeyValueItem[] = [];
      Object.entries(defaultValue).forEach(([key, value]) => {
        items.push({ key, value });
      });
      setKeyValueItems(items);
    } else if (keyValueItems.length === 0) {
      addEmptyKeyValueItem();
    }

    const maxAllowedOptionsValidation = field.validationRules?.find(
      (validationRule) =>
        validationRule.type === ValidationTypeEnumResponse.MAX_ALLOWED_OPTIONS,
    );
    setMaxAllowedOptionsValidation(maxAllowedOptionsValidation);
  }, []);

  useEffect(() => {
    const keyValuePairs: KeyValuePairStringIntFieldValue = {};

    keyValueItems.forEach((keyValueItem) => {
      if (!keyValueItem.key) {
        return;
      }

      const existingKeys = Object.keys(keyValuePairs);

      if (existingKeys.includes(keyValueItem.key)) {
        return;
      }

      if (keyValueItem.value === undefined) {
        return;
      }

      keyValuePairs[keyValueItem.key] = keyValueItem.value;
    });

    const validationResult = onChangeValidator(keyValuePairs);

    if (validationResult) {
      if (!validationResult.isValid) {
        const errorMessage =
          validationResult.errorMessage ??
          (validationResult.errorMessageKey
            ? t(
                validationResult.errorMessageKey,
                validationResult.errorMessageOptions,
              )
            : t('validation.default'));

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(undefined);
      }
    }

    setIsAddButtonDisabled(
      maxAllowedOptionsValidation &&
        maxAllowedOptionsValidation.validationValue &&
        keyValueItems.length >= maxAllowedOptionsValidation.validationValue,
    );
  }, [keyValueItems]);

  return (
    <>
      {keyValueItems.map((item, index) => (
        <Grid container key={index} className={classes.row}>
          <Grid item xs={8}>
            <FormControl fullWidth variant="outlined" disabled={disabled}>
              <InputLabel>{title}</InputLabel>
              <Select
                native
                value={item.key}
                onChange={(e) => {
                  const key = e.target.value as string;
                  handleKeyChanged(index, key);
                }}
                label={title}
                fullWidth
              >
                <option aria-label="None" value="" />
                {getAvailableOptions(
                  options ?? undefined,
                  keyValueItems.map((item: KeyValueItem) => item.key),
                  item.key,
                ).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.value}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              type="number"
              variant="outlined"
              error={errorMessage !== undefined}
              fullWidth
              InputProps={buildNumberTextFieldInputProps(field)}
              value={item.value !== undefined ? item.value.toString() : ''}
              onChange={(e) => handleValueChange(index, e.target.value)}
              onBlur={(e) => handleValueChange(index, e.target.value)}
              className={classes.number}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      ))}
      {errorMessage && (
        <Typography className={classes.error}>{errorMessage}</Typography>
      )}
      {disabled !== true && (
        <Button
          variant="outlined"
          color="primary"
          component="label"
          size="large"
          fullWidth
          className={classes.button}
          onClick={addEmptyKeyValueItem}
          disabled={isAddButtonDisabled}
        >
          + {title}
        </Button>
      )}
    </>
  );
};

export { KeyValuePairStringIntField };
